/**
 * Research tab — per-charge jury-research panels. Faithful port of the
 * monolith's renderResearch / setResearchCharge / renderResearchContent.
 * All research prose is preserved verbatim from the RESEARCH database.
 */

import type { ChargeSlug } from '@/types';
import { store } from '@/app/store';
import { byId } from '@/ui/dom';
import { CHARGES } from '@/data/charges';
import { RESEARCH } from '@/data/research';

/**
 * Live-research population and citation-link decoration are separate features
 * ported elsewhere; they are attached to `window` by main.ts. Call them if
 * present so this module stays self-contained while preserving behavior.
 */
interface ResearchGlobals {
  applyCitationLinks?: (el: HTMLElement) => void;
  renderCachedLiveResearch?: () => void;
}
function researchGlobals(): ResearchGlobals {
  return window as unknown as ResearchGlobals;
}

export function renderResearch(): void {
  const state = store.getState();
  const tabs = byId('research-charge-tabs');
  if (tabs) {
    tabs.innerHTML = (Object.entries(CHARGES) as [ChargeSlug, (typeof CHARGES)[ChargeSlug]][])
      .map((e) => {
        const k = e[0];
        const c = e[1];
        return (
          '<button class="filter-chip' +
          (state.researchCharge === k ? ' active' : '') +
          '" onclick="setResearchCharge(\'' +
          k +
          '\')">' +
          c.icon +
          ' ' +
          c.label +
          '</button>'
        );
      })
      .join('');
  }
  renderResearchContent();
}

export function setResearchCharge(k: ChargeSlug): void {
  store.getState().researchCharge = k;
  store.save();
  renderResearch();
}

export function renderResearchContent(): void {
  const state = store.getState();
  const r = RESEARCH[state.researchCharge];
  if (!r) return;

  let html = '';
  r.sections.forEach((sec) => {
    html +=
      '<div class="card" style="margin-bottom:0;"><div class="card-title"><div class="cticon"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" stroke-width="2.5"><polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/></svg></div>' +
      sec.heading +
      '</div>';
    if (sec.items)
      sec.items.forEach((it) => {
        html +=
          '<div class="stat-row"><span>' +
          it.label +
          '</span><span class="stat-val">' +
          it.val +
          '</span></div>';
      });
    if (sec.content) html += '<div class="ritem">' + sec.content + '</div>';
    if (sec.items2) {
      html += '<div>';
      sec.items2.forEach((q, i) => {
        html += '<div class="ritem"><strong>' + (i + 1) + '.</strong> ' + q + '</div>';
      });
      html += '</div>';
    }
    html += '</div>';
  });

  const ch = CHARGES[state.researchCharge];
  html +=
    '<div class="card"><div class="card-title"><div class="cticon"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg></div>Strategy Note</div><div class="ritem">' +
    ch.notes +
    '</div></div>';
  // ── LIVE RESEARCH (public reference data only; no juror data transmitted) ──
  html +=
    '<div class="card" id="live-research-card"><div class="card-title"><div class="cticon"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg></div>Live Research <span style="font-weight:400;font-size:10px;color:#94a3b8;">(fetches public studies &amp; case law — never sends juror data)</span></div>' +
    '<button class="btn btn-secondary" style="width:100%;margin-bottom:8px;" onclick="fetchLiveResearch()" id="live-fetch-btn">Fetch latest research for this charge</button>' +
    '<div id="live-research-results"></div></div>';

  const content = byId('research-content');
  if (content) {
    content.innerHTML = html;
    const g = researchGlobals();
    if (g.applyCitationLinks) g.applyCitationLinks(content);
  }
  const g = researchGlobals();
  if (g.renderCachedLiveResearch) g.renderCachedLiveResearch();
}
